import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";
import api from "../../axiosInstance";
import { BUTTON_STYLES } from "../../styles/componentStyles";
import BlogCategoryModal from "./BlogCategoryModal";

function BlogCategories() {
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [categoryEdit, setCategoryEdit] = useState(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  const loadCategories = () => {
    setIsLoading(true);
    api
      .get("/api/admin/management/blog-categories")
      .then((res) => setCategories(res.data))
      .catch((err) => {
        console.error("Failed to load blog categories:", err);
        setError("Failed to load blog categories. Please try again.");
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const handleAddOpen = () => {
    setCategoryEdit(null);
    setModalOpen(true);
  };

  const handleEditOpen = (category) => {
    setCategoryEdit(category);
    setModalOpen(true);
  };

  const handleFormSuccess = (saved) => {
    setCategories((prev) => {
      const exists = prev.some((c) => c.id === saved.id);
      return exists ? prev.map((c) => (c.id === saved.id ? saved : c)) : [...prev, saved];
    });
  };

  const handleDeleteOpen = (category) => {
    setCategoryToDelete(category);
    setDeleteOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteOpen(false);
    setCategoryToDelete(null);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/api/admin/management/blog-categories/${categoryToDelete.id}`);
      setCategories((prev) => prev.filter((c) => c.id !== categoryToDelete.id));
      toast.success("Category deleted successfully!", {
        position: "top-right",
        autoClose: 3000,
      });
      setDeleteOpen(false);
      setCategoryToDelete(null);
    } catch (err) {
      // A category still referenced by a blog is blocked (409) with a real
      // message from the backend (how many blogs use it, and which) --
      // surfaced as-is rather than the generic failure string every other
      // delete screen in this portal falls back to today. Kept on screen a
      // little longer since it lists blog titles.
      if (err.response?.status === 409) {
        toast.error(
          err.response?.data?.errorMessage || "This category is still in use by one or more blogs.",
          { autoClose: 8000 },
        );
      } else {
        toast.error("Failed to delete the category. Please try again.");
      }
      console.error("Delete failed:", err);
    }
  };

  return (
    <>
      <Card sx={{ margin: "0 15px", boxShadow: 1 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Blog Categories
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddOpen}
              {...BUTTON_STYLES.primary}
            >
              Add Category
            </Button>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer sx={{ width: "98%", mx: "auto", mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ backgroundColor: "#17479e", color: "white", fontWeight: 600 }}>
                  Category Name
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "#17479e",
                    color: "white",
                    fontWeight: 600,
                    textAlign: "center",
                    minWidth: 120,
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} sx={{ textAlign: "center", py: 4 }}>
                    No blog categories yet.
                  </TableCell>
                </TableRow>
              ) : (
                categories.map((category) => (
                  <TableRow key={category.id} hover>
                    <TableCell>{category.name}</TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Edit category" arrow>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditOpen(category)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete category" arrow>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteOpen(category)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <BlogCategoryModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        categoryEdit={categoryEdit}
        onSuccess={handleFormSuccess}
      />

      {/* Simple Cancel/Delete confirmation -- no type-to-confirm, unlike the
          shared DeleteConfirmationDialog used elsewhere in the portal. The
          backend's existing 409-when-in-use check is unaffected; this only
          changes the confirmation step before that call is made. */}
      <Dialog open={deleteOpen} onClose={handleDeleteClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Delete Category?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{categoryToDelete?.name}"?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteClose} {...BUTTON_STYLES.secondary}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default BlogCategories;
